import { gql } from "utils/urql";
import { forwardRef, Ref } from "react";
import { ArticleLegacyHtmlFragment, LegacyHtmlTagName } from "types/api";
import useImperativeLazyLoad from "utils/useImperativeLazyLoad";
import styles from "./ArticleLegacyHtml.module.scss";
import cx from "classnames";
import parse from "style-to-js";

type LegacyHtmlTagNameString =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "div"
  | "blockquote"
  | "hr"
  | "figure"
  | "aside"
  | "ul"
  | "ol";

type LegacyHtmlElementRef = React.ElementRef<LegacyHtmlTagNameString>;

// TODO: Replace regular expressions with structured data from the API
const isCalloutClass = /^(.*?\b)?callout(\b.*?)?$/;
const isFullWidthClass = /^(.*?\b)?full\-width(\b.*?)?$/;
const isWrappingDivFullWidth = /^<\w+[^>]*?\bclass=("|.*?\b)full\-width("|\b.*?")/s;

const ArticleLegacyHtml = forwardRef<
  LegacyHtmlElementRef,
  ArticleLegacyHtmlFragment
>(function ArticleLegacyHtml(
  { tagName, idAttr, className, style, innerHtml },
  ref
) {
  const lazyRef = useImperativeLazyLoad<LegacyHtmlElementRef>();

  // Change legacyhtml <p> tags to <div>, as react removes any elements that
  // are invalid children of <p> on hydration (the closest thing to
  // documentation of this fact is the issue facebook/react#5479)
  if (tagName === LegacyHtmlTagName.P) {
    tagName = LegacyHtmlTagName.Div;
    className = cx(styles.paragraph, className);
  }

  const needsWrappingDiv =
    className.match(isCalloutClass) ||
    tagName === LegacyHtmlTagName.Ol ||
    tagName === LegacyHtmlTagName.Ul ||
    tagName === LegacyHtmlTagName.Blockquote;

  // Add .root to className, except if the top-level element is a callout, in
  // which case it needs to be wrapped in a <div className={styles.root}> for
  // the float styles to work, or if it's an element that needs left or right
  // margin or padding that would be overridden if it had @include articleColumnWidth
  if (!needsWrappingDiv) {
    className = cx(styles.root, className);

    const isFullWidth =
      className.match(isFullWidthClass) ||
      innerHtml.match(isWrappingDivFullWidth);

    className = cx(className, isFullWidth ? styles.fullWidth : styles.standard);
  }

  const Tag = `${tagName.toLowerCase()}` as LegacyHtmlTagNameString;

  // parse style attribute strings into objects
  // catch errors on misformatted styles to prevent
  // https://github.com/remarkablemark/style-to-js#invalid-declaration
  let parsedStyles = {};
  try {
    parsedStyles = parse(style, { reactCompat: true });
  } catch (error) {
    console.warn("Inline style syntax error");
  }

  const ret =
    tagName === LegacyHtmlTagName.Hr ? (
      <hr
        ref={ref as Ref<HTMLHRElement>}
        id={idAttr || undefined}
        className={className}
        style={parsedStyles}
      />
    ) : (
      <Tag
        id={idAttr || undefined}
        className={className}
        style={parsedStyles}
        dangerouslySetInnerHTML={{ __html: innerHtml }}
        ref={<T extends LegacyHtmlElementRef>(elem: T | null) => {
          if (!elem) return;
          lazyRef.current = elem;
          // Client-side injector passes down a ref of type function,
          // but Typescript makes us also handle for a ref from useRef.
          // so a ref can be either func | useRef-ish {current: any} | null.
          if (typeof ref === "function") {
            ref(elem);
          } else if (ref) {
            ref.current = elem;
          }
        }}
      />
    );
  return needsWrappingDiv ? (
    <div className={cx(styles.root, styles.standard)}>{ret}</div>
  ) : (
    ret
  );
});

export default ArticleLegacyHtml;

ArticleLegacyHtml.fragment = gql`
  fragment articleLegacyHtml on ArticleLegacyHtml {
    tagName
    idAttr
    className
    style
    innerHtml
  }
`;
